"use client";

import { useState } from "react";
import type { Column, RowData } from "@tanstack/react-table";
import type { RecordGridFeatures } from "@/components/ui/record-table/grid/record-grid-features";

import { distributeColumnWidths, resolveGrowMaxWidth } from "./grid-widths";

function sameWidths(
  previous: Map<string, number>,
  next: Map<string, number>,
): boolean {
  if (previous.size !== next.size) return false;
  for (const [id, width] of next) {
    if (previous.get(id) !== width) return false;
  }
  return true;
}

export function useColumnWidths<TData extends RowData>(
  leafColumns: Column<RecordGridFeatures, TData, unknown>[],
  resizedWidths: Record<string, number>,
  available: number,
): Map<string, number> {
  const distributable = leafColumns.map((column) => {
    const size = column.getSize();
    const isResized = resizedWidths[column.id] !== undefined;
    return {
      id: column.id,
      size,
      growMaxWidth: isResized
        ? size
        : resolveGrowMaxWidth(column.columnDef.meta),
    };
  });
  const widths = distributeColumnWidths(distributable, available);
  const [renderedWidths, setRenderedWidths] = useState(widths);

  if (sameWidths(renderedWidths, widths)) return renderedWidths;
  setRenderedWidths(widths);
  return widths;
}
